import { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { PlayerStatus, PlayerTypes } from '../types/hubspotVideo';
import { loadHubSpotVideoApi } from '../utils/loadPlayerApi';
// @ts-expect-error Not converted yet
import withPauseAndHelpLogic from '../higherOrderComponents/withPauseAndHelpLogic';
import { View } from 'foundations-components/components/View';
import { EnabledKeyboardKeys } from '../types/hubspotVideo';
import { jsx as _jsx } from "react/jsx-runtime";
function getHsVideoApi() {
  return window.hsVideoApi;
}
class HubSpotVideoPlayer extends PureComponent {
  constructor(...args) {
    super(...args);
    this.containerRef = /*#__PURE__*/createRef();
    this.playerRef = /*#__PURE__*/createRef();
    this.handleKeydownParentHotKey = event => {
      const enabledHotKeys = [EnabledKeyboardKeys.SPACE, EnabledKeyboardKeys.ARROW_LEFT, EnabledKeyboardKeys.ARROW_RIGHT, EnabledKeyboardKeys.M, EnabledKeyboardKeys.F];
      const target = event.target;
      const isTextInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (!isTextInput && enabledHotKeys.includes(event.code)) {
        event.preventDefault();
        event.stopPropagation();
        if (event.code === EnabledKeyboardKeys.F) {
          var _this$playerRef$curre;
          if ((_this$playerRef$curre = this.playerRef.current) !== null && _this$playerRef$curre !== void 0 && _this$playerRef$curre.iframeEl) {
            void this.playerRef.current.iframeEl.requestFullscreen();
          }
        } else {
          this.playerRef.current.postMessageToPlayer('TRIGGER_KEYBOARD_SHORTCUT', event.code);
        }
      }
    };
    this.hasSetInitialTime = false;
    this.initVideoPlayer = () => {
      this.playerRef.current = getHsVideoApi().renderPlayer(this.containerRef.current, this.buildOptionsFromProps());
      if (this.playerRef.current) {
        this.bindVideoEvents();
        const {
          enableParentHotKeys
        } = this.props;
        if (enableParentHotKeys) {
          document.addEventListener('keydown', this.handleKeydownParentHotKey);
        }
      } else {
        console.warn('renderPlayer did not return a player instance, cannot bind events');
      }
    };
    this.handlePlay = () => {
      const {
        currentTime,
        onPlay
      } = this.props;
      if (this.playerRef.current && !this.hasSetInitialTime && typeof currentTime === 'number' && currentTime > 0) {
        this.playerRef.current.postMessageToPlayer('SET_CURRENT_TIME', currentTime);
        this.hasSetInitialTime = true;
      }
      onPlay === null || onPlay === void 0 || onPlay();
    };
    this.bindVideoEvents = () => {
      const {
        onPlay,
        onPause,
        onFinish,
        onReady,
        onProgress,
        onUpdateProgress,
        onError,
        onLoaded,
        onTimeUpdate,
        onPlayBackRateChange,
        onToggleAutoPlay,
        onTogglePictureInPicture,
        onLoading
      } = this.props;
      if (onLoaded) {
        this.playerRef.current.on('videoLoaded', onLoaded);
      }
      if (onReady) {
        this.playerRef.current.on('ready', onReady);
      }
      if (onPlay) {
        this.playerRef.current.on('play', this.handlePlay);
      }
      if (onPause) {
        this.playerRef.current.on('pause', onPause);
      }
      if (onFinish) {
        this.playerRef.current.on('ended', onFinish);
      }
      if (onProgress) {
        this.playerRef.current.on('progress', onProgress);
      }
      if (onUpdateProgress) {
        this.playerRef.current.on('progress', onUpdateProgress);
      }
      if (onTimeUpdate) {
        this.playerRef.current.on('timeupdate', onTimeUpdate);
      }
      if (onError) {
        this.playerRef.current.on('error', onError);
      }
      if (onPlayBackRateChange) {
        this.playerRef.current.on('ratechange', onPlayBackRateChange);
      }
      if (onToggleAutoPlay) {
        this.playerRef.current.on('autoplaytoggle', onToggleAutoPlay);
      }
      if (onTogglePictureInPicture) {
        this.playerRef.current.on('pictureinpicturetoggle', onTogglePictureInPicture);
      }
      if (onLoading) {
        this.playerRef.current.on('loading', onLoading);
      }
    };
  }
  componentDidMount() {
    loadHubSpotVideoApi().then(this.initVideoPlayer).catch(err => console.error(err));
  }
  updateVideoPlayer() {
    getHsVideoApi().updatePlayer(this.playerRef.current.id, this.buildOptionsFromProps());
  }
  componentWillUnmount() {
    if (this.handleKeydownParentHotKey) {
      document.removeEventListener('keydown', this.handleKeydownParentHotKey);
    }
  }
  componentDidUpdate(prevProps) {
    const {
      videoId,
      currentTime,
      playbackRate,
      enablePictureInPicture,
      autoPlay
    } = this.props;
    if (prevProps.videoId !== videoId) {
      this.hasSetInitialTime = false;
    }
    const hasPlayBackRateUpdated = this.playerRef.current && [PlayerStatus.LOADING, PlayerStatus.READY].includes(this.playerRef.current.status) && prevProps.playbackRate !== playbackRate;
    if (hasPlayBackRateUpdated || prevProps.videoId !== videoId || enablePictureInPicture !== prevProps.enablePictureInPicture) this.updateVideoPlayer();
    if (typeof currentTime === 'number' && prevProps.currentTime !== currentTime) {
      this.playerRef.current.postMessageToPlayer('SET_CURRENT_TIME', currentTime);
    }
    if (typeof playbackRate === 'number' && prevProps.playbackRate !== playbackRate) {
      this.playerRef.current.postMessageToPlayer('SET_PLAYBACK_RATE', playbackRate);
    }
    if (this.playerRef.current && prevProps.autoPlay !== autoPlay && typeof autoPlay === 'boolean') {
      this.playerRef.current.postMessageToPlayer('TRIGGER_AUTOPLAY', autoPlay);
    }
  }
  buildOptionsFromProps() {
    const {
      env,
      region,
      portalId,
      playerType,
      renderContext,
      autoPlay,
      hiddenControls,
      maxHeight,
      objectFit,
      playbackRate,
      enablePictureInPicture,
      subtitleLanguage,
      enableVideoFrameHotKeys,
      enableParentHotKeys,
      enableAutoPlayToggle
    } = this.props;
    return {
      id: this.props.videoId,
      env,
      region,
      portalId,
      playerType: enablePictureInPicture ? playerType : PlayerTypes.ACADEMY_EMBEDDED,
      renderContext,
      autoPlay,
      hiddenControls,
      maxHeight,
      objectFit,
      playButtonColor: this.props.color,
      muted: this.props.mutedByDefault,
      playbackRate,
      enablePictureInPicture,
      enableVideoFrameHotKeys,
      subtitleLanguage,
      enableAutoPlayToggle,
      enableParentHotKeys
    };
  }
  render() {
    return (
      /*#__PURE__*/
      // prevent responsive sizing preventing clicks below video
      _jsx(View, {
        overflow: "hidden",
        children: /*#__PURE__*/_jsx("div", {
          className: "hubspot-video-player",
          ref: this.containerRef
        })
      })
    );
  }
}
HubSpotVideoPlayer.propTypes = {
  videoId: PropTypes.number.isRequired
};
HubSpotVideoPlayer.defaultProps = {
  env: 'prod',
  portalId: 53,
  playerType: PlayerTypes.ACADEMY_IN_APP,
  renderContext: 'ui-addon-video-player',
  enableVideoFrameHotKeys: false
};
export default withPauseAndHelpLogic(HubSpotVideoPlayer);